import * as Dialog from '@radix-ui/react-dialog';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import AlarmIcon from '../../assets/icons/alarm.svg?react';
import CloseIcon from '../../assets/icons/close.svg?react';
import NotificationIcon from '../../assets/icons/notification.svg?react';
import {
  AlarmTriggerState,
  NotificationEvent,
  useGetAlarmQuery,
  useGetMachineQuery,
} from '../../gql/generated';
import { cn } from '../../utils/className';
import { UNKNOWN_MACHINE_LABEL } from '../../utils/machineDisplay';
import {
  notificationAlarmLabel,
  notificationMembers,
} from '../../utils/notificationMembers';
import { NotificationDetailContent } from './NotificationDetailContent';

interface NotificationSidePanelProps {
  /** Whether the panel is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** The notification event to display */
  notification: NotificationEvent | null;
}

const triggerStateLabel: Record<AlarmTriggerState, string> = {
  [AlarmTriggerState.Triggered]: 'Pulse alert triggered',
  [AlarmTriggerState.Resolved]: 'Pulse alert resolved',
};

/**
 * NotificationSidePanel - Side panel for viewing notification details
 *
 * Displays notification information including machine name, alarm details,
 * history chart, and trigger configuration.
 */
export function NotificationSidePanel({
  open,
  onOpenChange,
  notification,
}: NotificationSidePanelProps) {
  const { data: machineData } = useGetMachineQuery({
    variables: { id: notification?.machineId ?? '' },
    skip: !notification?.machineId,
  });

  const { data: alarmData } = useGetAlarmQuery({
    variables: { id: notification?.alarmId ?? '' },
    skip: !notification?.alarmId,
  });

  const members = useMemo(
    () => (notification ? notificationMembers(notification) : []),
    [notification]
  );

  const machine = machineData?.machine;
  const alarm = alarmData?.alarm;

  const { formattedDate, formattedTime } = useMemo(() => {
    if (!notification?.timeUtc) {
      return { formattedDate: null, formattedTime: null };
    }

    const date = new Date(notification.timeUtc);
    return {
      formattedDate: date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
      formattedTime: date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  }, [notification]);

  if (!notification || !open) return null;

  const machineName =
    notification.machineName || machine?.displayName || UNKNOWN_MACHINE_LABEL;
  const alarmName =
    notificationAlarmLabel(notification) ||
    alarm?.name ||
    notification.subject ||
    'Pulse alert';

  return createPortal(
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {/* Backdrop overlay */}
      <Dialog.Overlay
        className='data-[state=open]:animate-in data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed
          inset-0 z-50 bg-black/50'
      />

      {/* Side panel content */}
      <Dialog.Content
        className={cn(
          // Base positioning
          'fixed z-50',
          // Mobile: Full screen
          'inset-0 h-full w-full',
          // Desktop: Right side panel
          'md:top-0 md:right-0 md:bottom-auto md:left-auto md:h-full md:w-140',
          // Styling
          'flex flex-col bg-white shadow-xl',
          // Animations
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          `md:data-[state=closed]:slide-out-to-right
          md:data-[state=open]:slide-in-from-right`
        )}
        aria-describedby='notification-panel-description'
      >
        {/* Custom Header */}
        <div
          className='border-border-primary flex items-start justify-between
            border-b p-4'
        >
          <div className='flex min-w-0 flex-1 flex-col gap-1'>
            {/* Notification Icon */}
            <NotificationIcon className='text-focus h-6 w-6' />

            {/* Machine Name */}
            <Dialog.Title className='text-focus size-p1 font-bold'>
              {machineName}
            </Dialog.Title>

            {/* Timestamp subtitle */}
            <div className='text-secondary size-caption flex items-center gap-1'>
              <span>
                {triggerStateLabel[notification.alarmTriggerState]}{' '}
                {formattedDate}
              </span>
              <span>·</span>
              <span>{formattedTime}</span>
            </div>

            {/* Alarm Name Badge */}
            <div
              className='bg-surface-focus-support mt-1 flex w-fit items-center
                gap-2 rounded-xl px-2 py-1'
            >
              <AlarmIcon className='text-secondary h-3 w-3' />
              <span className='text-secondary size-caption'>{alarmName}</span>
            </div>
          </div>

          {/* Close Button */}
          <button
            className='text-secondary hover:text-primary shrink-0 cursor-pointer
              rounded-sm p-1 transition-colors focus:outline-none
              focus-visible:ring-0 focus-visible:outline-none'
            aria-label='Close panel'
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className='h-6 w-6' />
          </button>
        </div>

        {/* Content area with scroll */}
        <div className='flex-1 overflow-y-auto'>
          {members.map((member, index) => (
            <div
              // The graph inside latches its initial window on mount, so
              // another notification remounts the content
              key={`${notification.id}:${member.alarmId ?? index}`}
              className='border-border-primary border-b last:border-b-0'
            >
              {members.length > 1 && (
                <h4 className='size-p2 text-primary px-6 pt-4 font-bold'>
                  {member.alarmName || 'Pulse alert'}
                </h4>
              )}
              <NotificationDetailContent
                notification={notification}
                member={member}
                className='px-6 py-4'
              />
            </div>
          ))}
        </div>

        {/* Hidden description for accessibility */}
        <p id='notification-panel-description' className='sr-only'>
          Notification details for{' '}
          {triggerStateLabel[notification.alarmTriggerState]} on {machineName}
        </p>
      </Dialog.Content>
    </Dialog.Root>,
    document.body
  );
}
